import React, { useEffect, useState, useCallback, useRef } from "react";
import "./styles/tokens.css";
import "./styles/sections.css";
import "./styles/pages.css";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import Hero from "./components/sections/Hero/Hero";
import TrustStrip from "./components/sections/TrustStrip/TrustStrip";
import Features from "./components/sections/Features/Features";
import HowItWorks from "./components/sections/HowItWorks/HowItWorks";
import Security from "./components/sections/Security/Security";
import StatsBand from "./components/sections/StatsBand/StatsBand";
import Roadmap from "./components/sections/Roadmap/Roadmap";
import NotifySection from "./components/sections/NotifySection/NotifySection";
import FinalCTA from "./components/sections/FinalCTA/FinalCTA";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import DeleteAccount from "./pages/DeleteAccount/DeleteAccount";

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('sq-theme') || 'dark');
  const [currentPage, setCurrentPage] = useState(() => {
    const path = window.location.pathname.replace('/', '');
    return ['terms', 'privacy', 'delete'].includes(path) ? path : 'home';
  });
  const revealObsRef = useRef(null);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem('sq-theme', theme);
  }, [theme]);

  // History routing
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace('/', '');
      setCurrentPage(['terms', 'privacy', 'delete'].includes(path) ? path : 'home');
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Scroll reveal
  useEffect(() => {
    if (revealObsRef.current) revealObsRef.current.disconnect();
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('vis');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    revealObsRef.current = obs;

    const t = setTimeout(() => {
      document.querySelectorAll('.reveal:not(.vis)').forEach(el => obs.observe(el));
    }, 100);
    return () => { clearTimeout(t); obs.disconnect(); };
  }, [currentPage]);

  const navigateTo = useCallback((page) => {
    const path = page === 'home' ? '/' : '/' + page;
    window.history.pushState(null, '', path);
    setCurrentPage(page);
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = useCallback((id) => {
    if (currentPage !== 'home') {
      window.history.pushState(null, '', '/');
      setCurrentPage('home');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentPage]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} navigateTo={navigateTo} scrollToSection={scrollToSection} />
      {currentPage === "home" ? (
        <>
          <Hero />
          <TrustStrip />
          <Features />
          <HowItWorks />
          <Security />
          <StatsBand />
          <Roadmap />
          <NotifySection />
          <FinalCTA />
        </>
      ) : currentPage === "terms" ? (
        <TermsOfService navigateTo={navigateTo} />
      ) : currentPage === "privacy" ? (
        <PrivacyPolicy navigateTo={navigateTo} />
      ) : currentPage === "delete" ? (
        <DeleteAccount navigateTo={navigateTo} />
      ) : null}
      <Footer navigateTo={navigateTo} scrollToSection={scrollToSection} />
    </>
  );
}
